import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../config/supabaseClient';
import './MatchHistory.css';

/* ─────────────────────────────────────────────────────────────
   MATCH HISTORY — reusable component that shows a player's
   recent matches with opponent name, result, score, and ELO
   change.

   Props:
   - userId        (string)  the player whose matches to load
   - limit         (number)  max matches to show (default 10)
   - onReportMatch (fn)      called when user clicks the CTA
                             in the empty state
   - viewMoreHref  (string)  if provided, renders a "View full
                             match history" footer row linking here
   ───────────────────────────────────────────────────────────── */

/* Converts a UTC timestamp to a human-friendly relative string */
function timeAgo(dateStr) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1)  return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24)  return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7)  return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/* Converts format code to a readable label */
function formatLabel(format) {
  const labels = { bo1: 'Single game', bo3: 'Best of 3', bo5: 'Best of 5', bo7: 'Best of 7' };
  return labels[format] || format;
}

/* ─────────────────────────────────────────────────────────────
   Props:
   - userId        (string)  the player whose matches to load
   - currentUserId (string)  the logged-in user (for cancel/edit ownership)
   - limit         (number)  max matches to show (default 10)
   - onReportMatch (fn)      called when user clicks the CTA in empty state
   - onEditMatch   (fn)      called with a match object to open edit modal
   - viewMoreHref  (string)  if provided, renders a footer link to full history
   ───────────────────────────────────────────────────────────── */
function MatchHistory({ userId, currentUserId, limit = 10, onReportMatch, onEditMatch, viewMoreHref }) {
  const [matches, setMatches]   = useState([]);
  const [loading, setLoading]   = useState(true);

  useEffect(() => {
    if (userId) fetchMatches();
  }, [userId]);

  async function fetchMatches() {
    setLoading(true);

    const { data } = await supabase
      .from('matches')
      .select(`
        id, format, winner_games_won, loser_games_won,
        winner_elo_change, loser_elo_change, played_at,
        status, reporter_id, points_per_game, is_doubles,
        winner:profiles!winner_id(id, username, elo_rating, matches_played),
        loser:profiles!loser_id(id, username, elo_rating, matches_played),
        winner2:profiles!winner2_id(id, username),
        loser2:profiles!loser2_id(id, username)
      `)
      .or(`winner_id.eq.${userId},loser_id.eq.${userId},winner2_id.eq.${userId},loser2_id.eq.${userId}`)
      .order('played_at', { ascending: false })
      .limit(limit);

    setMatches(data || []);
    setLoading(false);
  }

  async function handleCancel(matchId) {
    const { error } = await supabase.rpc('cancel_match', { p_match_id: matchId });
    if (error) {
      console.error('cancel_match error:', error);
      return;
    }
    fetchMatches();
    window.dispatchEvent(new CustomEvent('ranktt:matchResolved'));
  }

  // ── Loading ─────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="mh-loading">
        <span className="mh-loading-star">★</span>
        <span className="mh-loading-star">★</span>
        <span className="mh-loading-star">★</span>
      </div>
    );
  }

  // ── Empty state ─────────────────────────────────────────────
  if (matches.length === 0) {
    return (
      <div className="mh-empty">
        <p className="mh-empty-title">No matches yet</p>
        <p className="mh-empty-subtitle">
          Challenge someone to start your journey!
        </p>
        {onReportMatch && (
          <button className="mh-empty-btn" onClick={onReportMatch}>
            + Report a Match
          </button>
        )}
      </div>
    );
  }

  // ── Match list ──────────────────────────────────────────────
  return (
    <div className="mh-list">
      {matches.map(match => {
        const isDoubles = !!match.is_doubles;

        // For doubles, user may be winner OR winner2
        const isWin = isDoubles
          ? (match.winner?.id === userId || match.winner2?.id === userId)
          : match.winner?.id === userId;

        // Opponent(s) and partner for doubles
        let opponent = null;
        let partner  = null;
        let opponentLabel = null;
        if (isDoubles) {
          if (isWin) {
            partner  = match.winner?.id === userId ? match.winner2 : match.winner;
            const opps = [match.loser, match.loser2].filter(Boolean);
            opponentLabel = opps.map(o => o.username).join(' & ') || 'Unknown';
          } else {
            partner  = match.loser?.id === userId ? match.loser2 : match.loser;
            const opps = [match.winner, match.winner2].filter(Boolean);
            opponentLabel = opps.map(o => o.username).join(' & ') || 'Unknown';
          }
        } else {
          opponent = isWin ? match.loser : match.winner;
        }

        const myGames     = isWin ? match.winner_games_won : match.loser_games_won;
        const oppGames    = isWin ? match.loser_games_won  : match.winner_games_won;
        const eloChange   = isWin ? match.winner_elo_change : match.loser_elo_change;
        const isPending   = match.status === 'pending';
        const isConfirmed = match.status === 'confirmed' || !match.status;
        const isReporter  = match.reporter_id === currentUserId;

        return (
          <div key={match.id} className={`mh-row ${isPending ? 'mh-row-pending' : isWin ? 'mh-row-win' : 'mh-row-loss'}`}>

            {/* Result pill */}
            <span className={`mh-result ${isPending ? 'mh-result-pending' : isWin ? 'mh-result-win' : 'mh-result-loss'}`}>
              {isPending ? '…' : isWin ? 'W' : 'L'}
            </span>

            {/* Opponent + format/doubles info */}
            <div className="mh-opponent">
              {isDoubles ? (
                <>
                  <span className="mh-opponent-name">{opponentLabel}</span>
                  <span className="mh-format">2v2{partner ? ` · w/ ${partner.username}` : ''}</span>
                </>
              ) : (
                <>
                  {opponent?.username ? (
                    <Link to={`/profile/${opponent.username}`} className="mh-opponent-link">
                      {opponent.username}
                    </Link>
                  ) : (
                    <span className="mh-opponent-name mh-opponent-deleted">Deleted User</span>
                  )}
                  <span className="mh-format">{formatLabel(match.format)}</span>
                </>
              )}
            </div>

            {/* Status badge */}
            {!isConfirmed ? (
              <span className={`mh-status-badge mh-status-${match.status}`}>
                {match.status === 'pending'   ? 'Pending'   :
                 match.status === 'denied'    ? 'Declined'  :
                 match.status === 'cancelled' ? 'Cancelled' : match.status}
              </span>
            ) : <span className="mh-col-placeholder" />}

            {/* Score */}
            <span className="mh-score">{myGames}–{oppGames}</span>

            {/* ELO change */}
            {isConfirmed ? (
              <span className={`mh-elo ${eloChange >= 0 ? 'mh-elo-gain' : 'mh-elo-loss'}`}>
                {eloChange >= 0 ? `+${eloChange}` : eloChange}
              </span>
            ) : <span className="mh-col-placeholder" />}

            {/* Date or cancel/edit buttons */}
            {isPending && isReporter ? (
              <div className="mh-row-actions">
                {onEditMatch && !isDoubles && (
                  <button className="mh-action-btn mh-action-edit" onClick={() => onEditMatch(match)} title="Edit match request">✏</button>
                )}
                <button className="mh-action-btn mh-action-cancel" onClick={() => handleCancel(match.id)} title="Cancel match request">✕</button>
              </div>
            ) : (
              <span className="mh-time">{timeAgo(match.played_at)}</span>
            )}

          </div>
        );
      })}

      {/* "View full match history" footer row — only shown when viewMoreHref is passed */}
      {viewMoreHref && (
        <Link to={viewMoreHref} className="mh-view-more">
          View full match history →
        </Link>
      )}
    </div>
  );
}

export default MatchHistory;
